import { hoursToSeconds, minutesToSeconds } from 'date-fns';
import { useState, useMemo } from 'react';
import { makeid } from 'utils/helpers/makeid';
import { convertSecondsToTime } from 'utils/timeHelper';
import { useTranslation } from 'react-i18next';

import type { Dayjs } from 'dayjs';
import type { ICallHistory, ISplitCall } from './types';

export const useCallHistoryManagementModal = (editingItem: ICallHistory) => {
  const { t } = useTranslation();

  const initialItem: ISplitCall = {
    id: makeid(10),
    callId: editingItem.callId,
    type: editingItem.type,
    patientGuid: '',
    duration: 0,
    agent: '',
  };
  const [newItems, setNewItems] = useState<ISplitCall[]>([initialItem]);

  const leftTime = useMemo(() => {
    return (
      editingItem.duration - newItems.reduce((acc, i) => acc + i.duration, 0)
    );
  }, [editingItem.duration, newItems]);

  const updateNewItem = (id: string, updatedItem: Partial<ISplitCall>) => {
    setNewItems((prev) => {
      return prev.map((i) => {
        if (i.id === id) {
          return { ...i, ...updatedItem };
        }
        return i;
      });
    });
  };

  const createNewItem = () => {
    setNewItems((prev) => {
      return [
        ...prev,
        {
          ...initialItem,
          id: makeid(10),
        },
      ];
    });
  };

  const removeItem = (id: string) => {
    setNewItems((prev) => {
      return prev.filter((i) => i.id !== id);
    });
  };

  const leftTimeText = useMemo(() => {
    let text = '';
    if (leftTime < 0) {
      text += '-';
    }
    text += convertSecondsToTime(Math.abs(leftTime));
    return text;
  }, [leftTime]);

  const validationMessage = useMemo(() => {
    if (leftTime < 0) {
      return t('validation.originalCallShouldNotBeLessThanZero');
    }

    if (newItems.some((i) => !i.patientGuid || !i.duration)) {
      return t('validation.pleaseFillAllFields');
    }

    const hasCurrent = newItems.some(
      (i) =>
        i.patientGuid === editingItem.patient.guid &&
        i.type === editingItem.type,
    );
    if (hasCurrent) {
      return t('validation.originalCallPatientAndPlatformShouldDiffer');
    }
    if (
      newItems.length > 1 &&
      newItems.some((i) => {
        const found = newItems.find(
          (c) => i.patientGuid === c.patientGuid && i.id !== c.id,
        );
        if (!found) {
          return false;
        }
        if (found.type !== i.type) {
          return false;
        }
        if (
          found.patientGuid === editingItem.patient.guid &&
          found.type !== editingItem.type
        ) {
          return false;
        }
        return true;
      })
    ) {
      return t('validation.pleaseAddPatientOnlyOnce');
    }
    if (
      newItems.length > 1 &&
      newItems.every((i) => i.patientGuid === editingItem.patient.guid)
    ) {
      return t('validation.originalCallPatientAndPlatformShouldDiffer');
    }
    if (newItems.length === 0) {
      return t('validation.pleaseAddAtLeastOneCall');
    }
    return null;
  }, [newItems, leftTime]);

  const onTimeChange = (value: Dayjs | null, newItem: ISplitCall) => {
    if (!value) {
      updateNewItem(newItem.id, {
        duration: 0,
      });
      return;
    }
    let seconds = 0;
    seconds += hoursToSeconds(value.get('hours'));
    seconds += minutesToSeconds(value.get('minutes'));
    seconds += value.get('seconds');
    updateNewItem(newItem.id, {
      duration: seconds,
    });
  };

  return {
    newItems,
    leftTime,
    leftTimeText,
    validationMessage,
    createNewItem,
    updateNewItem,
    onTimeChange,
    removeItem,
  };
};
